import React, { useState, useEffect, useCallback, useContext } from "react";
import Box from "@mui/material/Box";
import filter from "lodash/filter";
import some from "lodash/some";
import Grid from "@mui/material/Grid";
import LoadingSpinner from "components/loadingSpinner";
import AdvanceFilterModal from "components/modals/advanceFilterModal";
import Emptystate from "components/emptyState";
import { useAppContext } from "context/appContext";
import ProductsCard from "components/homepage/productCard";
import { useStyles } from "./style";
import { useTheme } from "@emotion/react";
import constants from "../../constants/constants.json";
import Notification from "components/snackbar";
import { ProductsContext } from "../../context/productsContext";

import {
  sortCards,
  getOverviewLevel1,
  userFavorites,
} from "./homePage.services";

import { useNavigate } from "react-router";
import PropsTypes from "prop-types";
import { useAuth0 } from "@auth0/auth0-react";
import { getToken } from "utils/loadAuth";
import HomePagePagination from "components/homepage/paginantion";
const pageSize = 8;

const HomePage = ({ isNavbarOpen }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const {
    searchQuery,
    sortOption,
    filterOption,
    setShowAdvanceFilterOptions,
    showAdvanceFilterOptions,
    setFilterOption,
  } = useAppContext();

  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const [products, setProducts] = useState(null);
  const [copiedProducts, setCopiedProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(filterOption.currentPage);
  const [totalItems, setTotalItems] = useState(1);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { setGWProducts } = useContext(ProductsContext);

  const [notification, setNotification] = useState({
    type: "", // error, success
    message: "",
  });

  const notificationProps = {
    open: notification.message.length > 0,
    setOpen: () => setNotification({ type: "", message: "" }),
    message: notification.message,
    severity: notification.type,
  };

  const handlePageChange = useCallback(
    (_, page) => {
      const currentPage = Math.max(page, 1);
      setCurrentPage(currentPage);
      setFilterOption({
        ...filterOption,
        currentPage,
      });
    },
    [filterOption, setFilterOption]
  );

  const getProductLinks = (links) => links.map((l) => l.function_name);

  const getProductTypes = useCallback(
    (products) => {
      const types = [];
      const links = [];

      const productTypesAndLinks = products.map((p) => ({
        type: p.product_type,
        links: getProductLinks(p.link_name),
      }));

      productTypesAndLinks.forEach((p) => {
        p.type !== constants.allProducts && types.push(p.type);
        links.push(...p.links);
      });

      const allProType = [...new Set(types)];
      const allProdLinks = [...new Set(links)];

      setFilterOption({
        ...filterOption,
        allproductType: allProType,
        allLinks: allProdLinks,
        allProducts: products,
      });
    },
    [filterOption, setFilterOption]
  );

  const navigateToLink = (product, link) => {
     if (link.function_name === "Smart Repair") {
      navigate("/smart-repair");
    } else {
      navigate(
        `/overview/entry/${product.product_id}/${Number(link.function_id)}`
      );
    }
  };

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });
      if (token) {
        const res = await getOverviewLevel1({ React, token });
        const tableList = res.data;

        if (tableList) {
          tableList.items = tableList.items.filter(
            (item) => item.link_name.length > 0
          );

          tableList.items = tableList.items.sort((a, b) =>
            a.product_name?.localeCompare(b?.product_name)
          );
          setProducts(tableList?.items);
          setGWProducts(tableList?.items);
          setCopiedProducts(tableList);
          setTotalItems(tableList.total);
          getProductTypes(tableList.items);
        }
      } else {
        setIsTokenExpired(true);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
    getProductTypes,
    setGWProducts,
  ]);

  const applyProductFilters = (newItems) => {
    if (filterOption.links.length > 0 && filterOption.productType.length > 0) {
      newItems = filter(
        newItems,
        (item) =>
          filterOption.productType.includes(item.product_type) &&
          some(item.link_name, (link) =>
            filterOption.links.includes(link.function_name)
          )
      );
    } else {
      newItems = filter(
        newItems,
        (item) =>
          filterOption.productType.includes(item.product_type) ||
          some(item.link_name, (link) =>
            filterOption.links.includes(link.function_name)
          )
      );
    }
    return newItems;
  };

  const getFilteredProducts = ({
    newItems,
    totalItem,
    copiedProducts,
    filterOption,
  }) => {
    if (filterOption?.clearFilters) {
      newItems = copiedProducts.items;
    } else if (filterOption?.advanceFilter && filterOption?.filterByFavorites) {
      let copiedItems = applyProductFilters(newItems);
      newItems = copiedItems.filter((product) => product.isFavorated);
      totalItem = newItems.length;
    } else if (
      filterOption?.advanceFilter &&
      !filterOption?.filterByFavorites
    ) {
      newItems = applyProductFilters(newItems);
      totalItem = newItems.length;
    } else if (filterOption?.filterByFavorites) {
      newItems = copiedProducts.items.filter((product) => product.isFavorated);
      totalItem = newItems.length;
    } else {
      newItems = copiedProducts.items;
    }
    newItems = sortCards({
      data: newItems,
      sortOption,
    });
    return { newItems, totalItem };
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCopiedProducts({
      ...copiedProducts,
      items: sortCards({ data: copiedProducts.items, sortOption }),
    });

    handlePageChange(undefined, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption]);

  useEffect(
    () => setCurrentPage(filterOption.currentPage),
    [filterOption.currentPage]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (copiedProducts?.items) {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = Math.min(
        startIndex + pageSize - 1,
        copiedProducts.total - 1
      );

      let newItems = copiedProducts.items;
      let totalItem = copiedProducts.items.length;

      const data = getFilteredProducts({
        newItems,
        totalItem,
        copiedProducts,
        filterOption,
        sortOption,
      });
      newItems = data.newItems;
      totalItem = data.totalItem;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        newItems = newItems.filter((p) =>
          p.product_name.toLowerCase().includes(query)
        );
        totalItem = newItems.length;
      }
      newItems = newItems.slice(startIndex, endIndex + 1);

      setTotalItems(totalItem);
      setProducts(newItems);

      if (!isLoading && totalItem <= pageSize) {
        setCurrentPage(1);
        setFilterOption({
          ...filterOption,
          currentPage: 1,
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    searchQuery,
    filterOption.productType,
    filterOption.links,
    sortOption,
    isLoading,
    filterOption.filterByFavorites,
  ]);

  const classes = useStyles({
    isNavbarOpen,
    theme,
    count: Math.ceil(totalItems / pageSize),
  });

  const productTitleLength = 5;

  const getTitle = (product) => {
    if (product.product_name?.length > productTitleLength) {
      return product.product_name;
    }
    return "";
  };

  const isNeedOfWordBreak = (product) => {
    if (product.product_name?.includes(" ")) {
      return "break-word";
    }
    return "break-all";
  };

  const getProductType = (product) => {
    if (product?.product_type) {
      return product.product_type;
    }
    return "";
  };

  const renderProducts = (product) => {
    return (
      <Grid item xs={12} md={3} sm={6} key={product.product_id}>
        <ProductsCard
          theme={theme}
          products={copiedProducts}
          product={product}
          getToken={getToken}
          getAccessTokenSilently={getAccessTokenSilently}
          loginWithRedirect={loginWithRedirect}
          user={user}
          userFavorites={userFavorites}
          setCopiedProducts={setCopiedProducts}
          getTitle={getTitle}
          getProductType={getProductType}
          navigateToLink={navigateToLink}
          isNeedOfWordBreak={isNeedOfWordBreak}
          setNotification={setNotification}
        />
      </Grid>
    );
  };

  if (isTokenExpired || isLoading || !products) {
    return (
      <Box className={classes.loadingWrapper}>
        <LoadingSpinner loadingMessage="Loading..." />
      </Box>
    );
  }

  return (
    <Box className={classes.cardsWrapper} data-testid="homePage">
      {products.length > 0 ? (
        <Box className={classes.container}>
          <Grid
            className={classes.productWrapper}
            container
            direction={"row"}
            justifyContent={"flex-start"}
            spacing={2}
          >
            {products.map((p) => renderProducts(p))}
          </Grid>
        </Box>
      ) : (
        <Emptystate
          title={constants.noResults}
          message={constants.noResultsMsg}
          isNavbarOpen={isNavbarOpen}
        />
      )}

      <HomePagePagination
        isNavbarOpen={isNavbarOpen}
        theme={theme}
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
      />

      <AdvanceFilterModal
        data-testid="homePage.AdvanceFilterModal"
        handleClose={() => setShowAdvanceFilterOptions((prev) => !prev)}
        isOpen={showAdvanceFilterOptions}
        handlePageChange={handlePageChange}
      />

      <Notification {...notificationProps} />
    </Box>
  );
};

HomePage.propTypes = {
  isNavbarOpen: PropsTypes.bool,
};
export default HomePage;
